import React, { useState, useEffect } from 'react';
import './Sidebar.css';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import { Collapse } from '@mui/material';

import { 
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import {
  DashboardRounded,
  AssignmentRounded,
  BuildRounded,
  GroupsRounded,
  ForumRounded,
  ChevronRightRounded,
  ExpandMoreRounded
} from '@mui/icons-material';

const Sidebar = ({ isOpen, toggleSidebar, toggleSecondarySidebar }) => {
    const [activeItem, setActiveItem] = useState(null);
    const [userInfo, setUserInfo] = useState(null);
    const [permissions, setPermissions] = useState({});
    const [expandedCategory, setExpandedCategory] = useState('orders');
    const navigate = useNavigate();
    const location = useLocation();

    const isActivePath = (path) => location.pathname.startsWith(path);

    // Wait for orgId become available in localStorage before proceed
    useEffect(() => {
        fetchSelectedOrg();
    }, [localStorage.getItem('selectedOrg')]);

    const sleep = ms => new Promise(r => setTimeout(r, ms));

    // Proceed only if selectedOrg is available, or retry in 10 ms
    const fetchSelectedOrg = async () => {
        const orgId = localStorage.getItem('selectedOrg');
        if (orgId != null) {
            fetchUserPermissions();
        } else {
            await sleep(10);
            fetchSelectedOrg();
        }
    }

    const fetchUserPermissions = async () => {
        const token = localStorage.getItem('token');
        const orgId = localStorage.getItem('selectedOrg');

        if (!token) {
            handleLogout();
            return;
        }
        try {
    
            const response = await axios.get(`${API_BASE_URL}/org/all-permissions`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { selectedOrg: orgId },
            });
            if (response.data != null) {
                setPermissions(response.data);
            }
            
            
        } catch (error) {
            console.error('Error fetching permissions:', error);
            handleLogout();
        }
    };

    const handleCategoryClick = (category) => {
        setExpandedCategory(prev => prev === category ? null : category);
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('selectedOrg');
        navigate('/');
    };

    const transitionStyles = {
        transition: 'all 200ms ease-in-out',
        '&:hover': {
            bgcolor: '#f5f5f5',
            transform: 'translateX(4px)'
        }
    };

    const transitionStylesItem = {
        transition: 'all 200ms ease-in-out',
        '&:hover': {
            bgcolor: '#ffffff',
            transform: 'translateX(4px)'
        }
    };

    const hasOrderPermissions = (
        permissions.create_order === 1 ||
        permissions.current_order_read === 1 ||
        permissions.finished_order_read === 1 ||
        permissions.do_not_proceed_read === 1 ||
        permissions.recently_deleted_read === 1
    );
    
    const hasOrganizationPermissions = (
        permissions.org_settings_read === 1 ||
        permissions.org_member_read === 1 ||
        permissions.org_roles_read === 1 ||
        permissions.product_management_read === 1 ||
        permissions.create_order === 1
    );

     return (
        <Box sx={{
            width: 240,
            height: 'calc(100vh - 64px)',
            position: 'fixed',
            left: 0,
            top: 64,
            bgcolor: 'white',
            borderRight: '1px solid #e0e0e0',
            p: 2
        }}>
            <List>
                {/* Dashboard */}
                {permissions.dashboard === 1 && (
                    <ListItem 
                        button 
                        component="a"
                        href="/#/dashboard"
                        onClick={() => navigate('/dashboard')}
                        sx={{
                            borderRadius: '10px',
                            ...transitionStyles,
                            borderLeft: isActivePath('/dashboard') ? '4px solid #1976d2' : 'none',
                            bgcolor: isActivePath('/dashboard') ? '#f0f4ff' : 'inherit',
                            '& .MuiListItemIcon-root': {
                                ...transitionStyles
                            }
                        }}
                    >
                        <ListItemIcon><DashboardRounded /></ListItemIcon>
                        <ListItemText primary="Dashboard" sx={{ cursor: 'pointer' }} />
                    </ListItem>
                )}
                
                <Divider sx={{ my: 2 }} />

                {/* Orders */}
                {hasOrderPermissions && (
                    <>
                        <ListItem 
                            button 
                            onClick={() => handleCategoryClick('orders')}
                            sx={{
                                borderRadius: '10px',
                                ...transitionStyles,
                                borderLeft: isActivePath('/order') ? '4px solid #1976d2' : 'none',
                                bgcolor: isActivePath('/order') ? '#f0f4ff' : 'inherit',
                                '& .MuiListItemIcon-root': {
                                    ...transitionStyles
                                }
                            }}
                        >
                            <ListItemIcon><AssignmentRounded /></ListItemIcon>
                            <ListItemText primary="Orders" sx={{ cursor: 'pointer' }} />
                            {expandedCategory === 'orders' ? <ExpandMoreRounded /> : <ChevronRightRounded />}
                        </ListItem>
                        
                        {expandedCategory === 'orders' && (
                            <Collapse in={expandedCategory === 'orders'} timeout={200} unmountOnExit>
                                <Box sx={{
                                    borderLeft: '2px solid #e0e0e0',
                                    marginLeft: 4,
                                    paddingLeft: 1,
                                    borderRadius: '0 10px 10px 0'
                                }}>
                                    {permissions.create_order === 1 && (
                                        <ListItem 
                                            button
                                            component="a"
                                            href="/#/order/create-order/0" 
                                            sx={{ 
                                                ...transitionStylesItem,

                                                pl: 0,
                                                borderRadius: '0 10px 10px 0',
                                                
                                            }}
                                            onClick={() => navigate('/order/create-order/0')}
                                        >
                                            <ListItemText 
                                                primary="Create New Order" 
                                                primaryTypographyProps={{
                                                    fontSize: '0.875rem',
                                                    color: isActivePath('/order/create-order') ? '#1976d2' : '#424242',
                                                    fontWeight: isActivePath('/order/create-order') ? 600 : 400
                                                }} 
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        </ListItem>
                                    )}
                                    {permissions.current_order_read === 1 && (
                                        <ListItem 
                                            button 
                                            component="a"
                                            href="/#/order/current-order"
                                            sx={{ 
                                                ...transitionStylesItem,

                                                pl: 0,
                                                borderRadius: '0 10px 10px 0',
                                                
                                            }}
                                            onClick={() => navigate('/order/current-order')}
                                        >
                                            <ListItemText 
                                                primary="Current Order" 
                                                primaryTypographyProps={{
                                                    fontSize: '0.875rem',
                                                    color: isActivePath('/order/current-order') ? '#1976d2' : '#424242',
                                                    fontWeight: isActivePath('/order/current-order') ? 600 : 400
                                                }} 
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        </ListItem>
                                    )}
                                    {permissions.finished_order_read === 1 && (
                                        <ListItem 
                                            button 
                                            component="a"
                                            href="/#/order/finished-order"
                                            sx={{ 
                                                ...transitionStylesItem,

                                                pl: 0,
                                                borderRadius: '0 10px 10px 0',
                                                
                                            }}
                                            onClick={() => navigate('/order/finished-order')}
                                        >
                                            <ListItemText 
                                                primary="Finished Order" 
                                                primaryTypographyProps={{
                                                    fontSize: '0.875rem',
                                                    color: isActivePath('/order/finished-order') ? '#1976d2' : '#424242',
                                                    fontWeight: isActivePath('/order/finished-order') ? 600 : 400
                                                }} 
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        </ListItem>
                                    )}
                                    {permissions.do_not_proceed_read === 1 && (
                                        <ListItem 
                                            button 
                                            component="a"
                                            href="/#/order/do-not-proceed"
                                            sx={{ 
                                                ...transitionStylesItem,

                                                pl: 0,
                                                borderRadius: '0 10px 10px 0',
                                                
                                            }}
                                            onClick={() => navigate('/order/do-not-proceed')}
                                        >
                                            <ListItemText 
                                                primary="Do Not Proceed" 
                                                primaryTypographyProps={{
                                                    fontSize: '0.875rem',
                                                    color: isActivePath('/order/do-not-proceed') ? '#1976d2' : '#424242',
                                                    fontWeight: isActivePath('/order/do-not-proceed') ? 600 : 400
                                                }} 
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        </ListItem>
                                    )}
                                    {permissions.recently_deleted_read === 1 && (
                                        <ListItem 
                                            button 
                                            component="a"
                                            href="/#/order/recently-deleted"
                                            sx={{ 
                                                ...transitionStylesItem,

                                                pl: 0,
                                                borderRadius: '0 10px 10px 0',
                                                
                                            }}
                                            onClick={() => navigate('/order/recently-deleted')}
                                        >
                                            <ListItemText 
                                                primary="Recently Deleted" 
                                                primaryTypographyProps={{
                                                    fontSize: '0.875rem',
                                                    color: isActivePath('/order/recently-deleted') ? '#1976d2' : '#424242',
                                                    fontWeight: isActivePath('/order/recently-deleted') ? 600 : 400
                                                }} 
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        </ListItem>
                                    )}
                                </Box>
                            </Collapse>
                        )}
                    </>
                )}

                {/* Tools */}
                {permissions.tools === 1 && (
                    <>
                        <ListItem 
                            button 
                            onClick={() => handleCategoryClick('tools')}
                            sx={{
                                borderRadius: '10px',
                                ...transitionStyles,
                                borderLeft: isActivePath('/tools') ? '4px solid #1976d2' : 'none',
                                bgcolor: isActivePath('/tools') ? '#f0f4ff' : 'inherit',
                                '& .MuiListItemIcon-root': {
                                    ...transitionStyles
                                }
                            }}
                        >
                            <ListItemIcon><BuildRounded /></ListItemIcon>
                            <ListItemText primary="Tools" sx={{ cursor: 'pointer' }} />
                            {expandedCategory === 'tools' ? <ExpandMoreRounded /> : <ChevronRightRounded />}
                        </ListItem>
                        
                        {expandedCategory === 'tools' && (
                            <Collapse in={expandedCategory === 'tools'} timeout={200} unmountOnExit>
                                <Box sx={{
                                    borderLeft: '2px solid #e0e0e0',
                                    marginLeft: 4,
                                    paddingLeft: 1,
                                    borderRadius: '0 10px 10px 0'
                                }}>
                                    <ListItem 
                                        button 
                                        sx={{ 
                                            ...transitionStylesItem,

                                            pl: 0,
                                            borderRadius: '0 10px 10px 0',
                                            
                                        }}
                                        onClick={() => navigate('/tools/quote-calculator')}
                                    >
                                        <ListItemText 
                                            primary="Quote Calculator" 
                                            primaryTypographyProps={{
                                                fontSize: '0.875rem',
                                                color: isActivePath('/tools/quote-calculator') ? '#1976d2' : '#424242',
                                                fontWeight: isActivePath('/tools/quote-calculator') ? 600 : 400
                                            }} 
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    </ListItem>

                                    <ListItem 
                                        button 
                                        sx={{ 
                                            ...transitionStylesItem,

                                            pl: 0,
                                            borderRadius: '0 10px 10px 0',
                                            
                                        }}
                                    >
                                        <ListItemText 
                                            primary="Calculator"
                                            primaryTypographyProps={{
                                                fontSize: '0.875rem',
                                                color: '#424242',
                                                fontWeight: 400
                                            }} 
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => window.open('calculator:', '_blank')}
                                        />
                                    </ListItem>

                                    <ListItem 
                                        button 
                                        sx={{ 
                                            ...transitionStylesItem,

                                            pl: 0,
                                            borderRadius: '0 10px 10px 0',
                                            
                                        }}
                                    >
                                        <ListItemText 
                                            primary="Google Maps"
                                            primaryTypographyProps={{
                                                fontSize: '0.875rem',
                                                color: '#424242',
                                                fontWeight: 400
                                            }}
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => window.open('https://maps.google.com', '_blank')}
                                        />
                                    </ListItem>

                                    <ListItem 
                                        button 
                                        sx={{ 
                                            ...transitionStylesItem,

                                            pl: 0,
                                            borderRadius: '0 10px 10px 0',
                                            
                                        }}
                                    >
                                        <ListItemText 
                                            primary="Email"
                                            primaryTypographyProps={{
                                                fontSize: '0.875rem',
                                                color: '#424242',
                                                fontWeight: 400
                                            }}
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => window.open('mailto:', '_blank')}
                                        />
                                    </ListItem>
                                </Box>
                            </Collapse>
                        )}
                    </>
                )}

                {/* Organization */}
                {hasOrganizationPermissions && (
                    <>
                        <ListItem 
                            button 
                            onClick={() => handleCategoryClick('org')}
                            sx={{
                                borderRadius: '10px',
                                ...transitionStyles,
                                borderLeft: isActivePath('/org') ? '4px solid #1976d2' : 'none',
                                bgcolor: isActivePath('/org') ? '#f0f4ff' : 'inherit',
                                '& .MuiListItemIcon-root': {
                                    ...transitionStyles
                                }
                            }}
                        >
                            <ListItemIcon><GroupsRounded /></ListItemIcon>
                            <ListItemText primary="Organization" sx={{ cursor: 'pointer' }} />
                            {expandedCategory === 'org' ? <ExpandMoreRounded /> : <ChevronRightRounded />}
                        </ListItem>
                        
                        {expandedCategory === 'org' && (
                            <Collapse in={expandedCategory === 'org'} timeout={200} unmountOnExit>
                                <Box sx={{
                                    borderLeft: '2px solid #e0e0e0',
                                    marginLeft: 4,
                                    paddingLeft: 1,
                                    borderRadius: '0 10px 10px 0'
                                }}>
                                    {permissions.org_settings_read === 1 && (
                                        <ListItem 
                                            button 
                                            component="a"
                                            href="/#/organization/settings"
                                            sx={{ 
                                                ...transitionStylesItem,

                                                pl: 0,
                                                borderRadius: '0 10px 10px 0',
                                                
                                            }}
                                            onClick={() => navigate('/organization/settings')}
                                        >
                                            <ListItemText 
                                                primary="Organization Settings" 
                                                primaryTypographyProps={{
                                                    fontSize: '0.875rem',
                                                    color: isActivePath('/organization/settings') ? '#1976d2' : '#424242',
                                                    fontWeight: isActivePath('/organization/settings') ? 600 : 400
                                                }} 
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        </ListItem>
                                    )}
                                    {permissions.org_roles_read === 1 && (
                                        <ListItem 
                                            button 
                                            component="a"
                                            href="/#/organization/roles"
                                            sx={{ 
                                                ...transitionStylesItem,

                                                pl: 0,
                                                borderRadius: '0 10px 10px 0',
                                                
                                            }}
                                            onClick={() => navigate('/organization/roles')}
                                        >
                                            <ListItemText 
                                                primary="Organization Roles" 
                                                primaryTypographyProps={{
                                                    fontSize: '0.875rem',
                                                    color: isActivePath('/organization/roles') ? '#1976d2' : '#424242',
                                                    fontWeight: isActivePath('/organization/roles') ? 600 : 400
                                                }} 
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        </ListItem>
                                    )}
                                    {permissions.org_members_read === 1 && (
                                        <ListItem 
                                            button 
                                            component="a"
                                            href="/#/organization/members"
                                            sx={{ 
                                                ...transitionStylesItem,

                                                pl: 0,
                                                borderRadius: '0 10px 10px 0',
                                                
                                            }}
                                            onClick={() => navigate('/organization/members')}
                                        >
                                            <ListItemText 
                                                primary="Organization Members" 
                                                primaryTypographyProps={{
                                                    fontSize: '0.875rem',
                                                    color: isActivePath('/organization/members') ? '#1976d2' : '#424242',
                                                    fontWeight: isActivePath('/organization/members') ? 600 : 400
                                                }} 
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        </ListItem>
                                    )}
                                    {permissions.product_management_read === 1 && (
                                        <ListItem 
                                            button 
                                            component="a"
                                            href="/#/organization/product-management"
                                            sx={{ 
                                                ...transitionStylesItem,

                                                pl: 0,
                                                borderRadius: '0 10px 10px 0',
                                                
                                            }}
                                            onClick={() => navigate('/organization/product-management')}
                                        >
                                            <ListItemText 
                                                primary="Product Management" 
                                                primaryTypographyProps={{
                                                    fontSize: '0.875rem',
                                                    color: isActivePath('/organization/product-management') ? '#1976d2' : '#424242',
                                                    fontWeight: isActivePath('/organization/product-management') ? 600 : 400
                                                }} 
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        </ListItem>
                                    )}
                                    {permissions.create_order === 1 && (
                                        <ListItem 
                                            button 
                                            component="a"
                                            href="/#/organization/customer-management"
                                            sx={{ 
                                                ...transitionStylesItem,

                                                pl: 0,
                                                borderRadius: '0 10px 10px 0',
                                                
                                            }}
                                            onClick={() => navigate('/organization/customer-management')}
                                        >
                                            <ListItemText 
                                                primary="Customer Management" 
                                                primaryTypographyProps={{
                                                    fontSize: '0.875rem',
                                                    color: isActivePath('/organization/customer-management') ? '#1976d2' : '#424242',
                                                    fontWeight: isActivePath('/organization/customer-management') ? 600 : 400
                                                }} 
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        </ListItem>
                                    )}
                                </Box>
                            </Collapse>
                        )}
                    </>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Messages */}
                {permissions.messages === 1 && (
                    <ListItem 
                        button 
                        onClick={() => navigate('/chat')}
                        sx={{
                            borderRadius: '4px',
                            '&:hover': { bgcolor: '#f5f5f5' }
                        }}
                    >
                        <ListItemIcon><ForumRounded /></ListItemIcon>
                        <ListItemText primary="Chat" sx={{ cursor: 'pointer' }} />
                    </ListItem>
                )}
            </List>

            <Box sx={{ 
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                p: 2,
                textAlign: 'center'
            }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="caption" sx={{ color: '#666' }}>
                    © {new Date().getFullYear()} Sign Blast Pty Ltd
                </Typography>
            </Box>
        </Box>
    );
};

export default Sidebar;
